// Preflop range parsing helpers (frontend-only hardcoded tables).
//
// Output convention:
// - hand keys: "AA", "AKs", "AKo" matching the 13x13 grid labels in `Solver.js`.
// - frequencies: 0..1

const RANKS_DESC = ["A", "K", "Q", "J", "T", "9", "8", "7", "6", "5", "4", "3", "2"];
const RANKS_ASC = ["2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K", "A"];
const rankIndexAsc = (r) => RANKS_ASC.indexOf(r);
const rankIndexDesc = (r) => RANKS_DESC.indexOf(r);
const SUITS = ["s", "h", "d", "c"]; // must match Solver.js

function normalizeHandKey(hand) {
  const s = hand.trim();
  if (!s) return null;

  // Pair
  if (/^[2-9TJQKA]{2}$/.test(s) && s[0] === s[1]) return s;

  // Suited/offsuit
  const m = s.match(/^([2-9TJQKA])([2-9TJQKA])(s|o)$/i);
  if (!m) return null;
  const r1 = m[1].toUpperCase();
  const r2 = m[2].toUpperCase();
  const so = m[3].toLowerCase();
  // Ensure higher rank first.
  if (rankIndexAsc(r1) >= rankIndexAsc(r2)) return `${r1}${r2}${so}`;
  return `${r2}${r1}${so}`;
}

function expandTokenToHandKeys(tokenRaw) {
  const token = tokenRaw.trim();
  if (!token) return [];
  const plus = token.endsWith("+");
  const base = plus ? token.slice(0, -1) : token;

  // Pair
  if (/^[2-9TJQKA]{2}$/.test(base) && base[0] === base[1]) {
    const r = base[0].toUpperCase();
    if (!plus) return [r + r];
    // e.g. 55+ => 55..AA
    const start = rankIndexAsc(r);
    if (start < 0) return [];
    return RANKS_ASC.slice(start).map((rk) => rk + rk);
  }

  // Suited/offsuit
  const m = base.match(/^([2-9TJQKA])([2-9TJQKA])(s|o)$/i);
  if (!m) return [];
  let r1 = m[1].toUpperCase();
  let r2 = m[2].toUpperCase();
  const so = m[3].toLowerCase();

  if (!plus) {
    const k = normalizeHandKey(`${r1}${r2}${so}`);
    return k ? [k] : [];
  }

  // Your `+` notation is *grid-directional*:
  // - suited (XYs+): same ROW (fixed high card) towards diagonal (stronger kickers)
  // - offsuit (XYo+): same COLUMN (fixed high card) towards diagonal (stronger kickers)
  // - pair (XX+): diagonal up-left (bigger pairs)
  //
  // We implement this using the same rank ordering as the 13x13 grid (RANKS_DESC).

  // Ensure r1 is the higher rank (grid expects high card first in notation).
  if (rankIndexDesc(r1) === -1 || rankIndexDesc(r2) === -1) return [];
  if (rankIndexDesc(r1) > rankIndexDesc(r2)) {
    const tmp = r1;
    r1 = r2;
    r2 = tmp;
  }

  const out = [];
  const highIdx = rankIndexDesc(r1); // smaller = stronger
  const lowIdx = rankIndexDesc(r2);

  if (so === "s") {
    // Row expansion: K5s+ => K5s,K6s,...,KQs (towards diagonal: kicker gets stronger => idx decreases)
    for (let kickerIdx = lowIdx; kickerIdx > highIdx; kickerIdx--) {
      const kicker = RANKS_DESC[kickerIdx];
      out.push(`${r1}${kicker}s`);
    }
    return out;
  }

  // Offsuit column expansion: KTo+ => KTo,KJo,KQo (towards diagonal: the *other* rank gets stronger)
  for (let otherIdx = lowIdx; otherIdx > highIdx; otherIdx--) {
    const other = RANKS_DESC[otherIdx];
    out.push(`${r1}${other}o`);
  }
  return out;
}

function generateAllCombosForHandLabel(label) {
  const combos = [];
  if (!label) return combos;
  if (label.length === 2) {
    const r = label[0];
    for (let i = 0; i < SUITS.length; i++) {
      for (let j = i + 1; j < SUITS.length; j++) {
        combos.push(`${r}${SUITS[i]}${r}${SUITS[j]}`);
      }
    }
    return combos;
  }
  if (label.length === 3 && (label.endsWith("s") || label.endsWith("o"))) {
    const r1 = label[0], r2 = label[1], so = label[2];
    if (so === "s") {
      for (const s of SUITS) combos.push(`${r1}${s}${r2}${s}`);
      return combos;
    }
    // offsuit
    for (let i = 0; i < SUITS.length; i++) {
      for (let j = 0; j < SUITS.length; j++) {
        if (i !== j) combos.push(`${r1}${SUITS[i]}${r2}${SUITS[j]}`);
      }
    }
    return combos;
  }
  return combos;
}

/**
 * Parse a compact range string into a map of handKey -> frequency (0..1).
 *
 * Supported examples:
 * - "55+"
 * - "44:0.28"
 * - "A2s+"
 * - "K5s+,K4s:0.17"
 * - "T9s,T8s:0.42"
 * - "ATo+"
 */
export function parseRangeSpecToFreqMap(spec) {
  const s = (spec || "").replace(/\s+/g, "");
  const rawTokens = s.split(",").map((x) => x.trim()).filter(Boolean);

  // First pass: determine which tokens get an explicit frequency.
  // IMPORTANT: freq applies to the exact token only (no backward propagation).
  const assigned = new Array(rawTokens.length).fill(null); // freq or null
  const handsOnly = new Array(rawTokens.length).fill(null); // token without ":freq"

  for (let i = 0; i < rawTokens.length; i++) {
    const t = rawTokens[i];
    const parts = t.split(":");
    if (parts.length === 2) {
      const handPart = parts[0];
      const freq = Number(parts[1]);
      handsOnly[i] = handPart;
      if (!Number.isFinite(freq)) continue;
      assigned[i] = Math.max(0, Math.min(1, freq));

    } else {
      handsOnly[i] = t;
    }
  }

  // Second pass: build the freq map with defaults (=1.0) where not assigned.
  const freqMap = {};
  for (let i = 0; i < rawTokens.length; i++) {
    const handToken = handsOnly[i];
    if (!handToken) continue;
    const freq = assigned[i] == null ? 1.0 : assigned[i];
    const keys = expandTokenToHandKeys(handToken);
    for (const k of keys) {
      if (!k) continue;
      freqMap[k] = freq;
    }
  }
  return freqMap;
}

export function buildEmpty13x13GridCell(label, type, actionsLen) {
  return {
    label,
    type,
    inRange: false,
    frequency: 0,
    frequencies: new Array(actionsLen).fill(0),
    ev: 0,
    equity: 0,
    combos: [],
  };
}

export function build13x13GridFromHandFreqMap(handFreqMap, actionLabel = "Open") {
  const actions = [actionLabel];
  const grid = Array(13)
    .fill(null)
    .map(() => Array(13).fill(null));

  for (let row = 0; row < 13; row++) {
    for (let col = 0; col < 13; col++) {
      const r1 = RANKS_DESC[row];
      const r2 = RANKS_DESC[col];
      const label =
        row === col ? `${r1}${r1}` : row < col ? `${r1}${r2}s` : `${r2}${r1}o`;
      const type = row === col ? "pair" : row < col ? "suited" : "offsuit";

      const f = Number(handFreqMap?.[label] ?? 0);
      const openFreq = Number.isFinite(f) ? Math.max(0, Math.min(1, f)) : 0;

      const combos = generateAllCombosForHandLabel(label).map((comboHand) => ({
        hand: comboHand,
        frequencies: [openFreq],
        ev: 0,
        equity: 0,
      }));

      grid[row][col] = {
        label,
        type,
        inRange: openFreq > 0.001,
        frequency: openFreq,
        frequencies: [openFreq],
        ev: 0,
        equity: 0,
        combos,
      };
    }
  }

  return { actions, grid };
}

